import {
  Box,
  List,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import TypographyText from '../../../components/material-ui/table/TypographyText';
import { useTranslation } from 'react-i18next';
import CusSwitch from '../../../components/material-ui/CusSwitch';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import { options0To100Percent, TEST_MODE } from '../../../enums/enums';
import CusButton from '../../../components/material-ui/CusButton';
import useToast from '../../../hooks/useToast';
import { API_SETTING } from '../../../api/setting';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import AllCheckbox from '../../../components/material-ui/AllCheckbox';
import CusInput from '../../../components/material-ui/CusInput';
import GambitProvidersListRow from './GambitProvidersListRow';
import { mockProvidersS } from '../../../enums/mock';

export default function GambitProvidersList({ sourceId, providersHas, setProvidersHas }) {
  const { t } = useTranslation();
  const { successToast } = useToast();

  const [selected, setSelected] = useState([]);

  const CusTableCell = ({ children, ...props }) => (
    <MyTableCell sx={{ py: 0 }} {...props}>
      {children}
    </MyTableCell>
  );

  const changeProviderParam = ({ enabled, percent, provider, customName }) => {
    return new Promise((rs, rj) => {
      API_SETTING.provider.option
        .set({ enabled, percent, provider, sourceId, customName })
        .then((e) => {
          successToast('Information updated successfully');
          rs(true);
        })
        .catch((e) => {
          console.error(e);
          rs(false);
        });
    });
  };

  const onChangeStatuses = () => {
    const newProviderHash = {};

    for (const providerName of selected) {
      if (providersHas.hasOwnProperty(providerName)) {
        const curProvider = providersHas[providerName];

        newProviderHash[providerName] = {
          enabled: !curProvider.enabled,
          percent: curProvider.percent
        };
      }
    }

    API_SETTING.provider.option
      .set({ sourceId: sourceId, providerList: newProviderHash })
      .then(() => {
        successToast('Information updated successfully');
        setSelected([]);
        setProvidersHas((prev) => ({ ...prev, ...newProviderHash }));
      })
      .catch(console.error);
  };

  const onChangeStatus = async (provName) => {
    const curProvider = providersHas[provName];
    const newEnabled = !curProvider.enabled;

    setProvidersHas((prev) => {
      return { ...prev, [provName]: { ...curProvider, enabled: newEnabled } };
    });

    const res = await changeProviderParam({
      enabled: newEnabled,
      percent: curProvider.percent,
      provider: provName
    });

    if (!res) {
      setProvidersHas((prev) => ({ ...prev, [provName]: { ...curProvider } }));
    }
  };

  const onChangeCommission = async (provName, newValue) => {
    const curProvider = providersHas[provName];
    setProvidersHas((prev) => {
      return { ...prev, [provName]: { ...curProvider, percent: +newValue } };
    });

    const res = await changeProviderParam({
      enabled: curProvider.enabled,
      percent: newValue,
      provider: provName
    });

    if (!res) {
      setProvidersHas((prev) => ({ ...prev, [provName]: { ...curProvider } }));
    }
  };

  useEffect(() => {
    if (TEST_MODE) {
      setProvidersHas(mockProvidersS);
      return;
    }

    if (!sourceId) return;

    API_SETTING.provider
      .list({ sourceId })
      .then((e) => {
        const providers = e.data.list;
        setProvidersHas(providers);
      })
      .catch(console.error);
  }, [sourceId]);

  const providersNames = Object.keys(providersHas);
  const totalGames = Object.values(providersHas)?.reduce((sum, provider) => {
    return sum + (provider.gameCount || 0);
  }, 0);

  return (
    <Stack gap={1.25}>
      <Stack direction={'row'} gap={1}>
        <Typography>
          <TypographyText>{t('List of providers')}</TypographyText>: {providersNames.length}
        </Typography>
      </Stack>

      <Stack gap={1}>
        <TableContainer variant="elevation">
          <Table size="small">
            <TableHead>
              <TableRow>
                <CusTableCell sx={{ py: 1 }} width={30}>
                  #
                </CusTableCell>
                <CusTableCell sx={{ py: 1 }} width={50}>
                  <AllCheckbox
                    allListId={providersNames}
                    chosenList={selected}
                    setChosenList={setSelected}
                  />
                </CusTableCell>
                <CusTableCell sx={{ py: 1 }}>
                  {t('Provider')}{' '}
                  <TypographyText color="textSecondary">({t('Number of games')})</TypographyText>
                </CusTableCell>
                <CusTableCell sx={{ py: 1 }}>{t('Currencies')}</CusTableCell>
                <CusTableCell sx={{ py: 1 }}>{t('Name')}</CusTableCell>
                <CusTableCell sx={{ py: 1 }} width={100}>
                  {t('Active')}
                </CusTableCell>
                <CusTableCell sx={{ py: 1 }} width={150}>
                  {t('Commission')}
                </CusTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providersNames.map((providerName, indx) => {
                const provider = providersHas[providerName];

                return (
                  <GambitProvidersListRow
                    key={indx}
                    indx={indx}
                    providerName={providerName}
                    provider={provider}
                    selected={selected}
                    setSelected={setSelected}
                    onChangeStatus={onChangeStatus}
                    onChangeCommission={onChangeCommission}
                    changeProviderParam={changeProviderParam}
                    setProvidersHas={setProvidersHas}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Typography>{t('Total games')}:</Typography>
          <TypographyText sx={{ mt: '3px' }}>{totalGames}</TypographyText>
        </Stack>
        <Stack direction={'row'} gap={1}>
          <CusButton
            text="Change status"
            variant="contained"
            disabled={!selected.length}
            onClick={onChangeStatuses}
          />

          <CusTooltip
            text={'The status (active/inactive) of all selected providers will be reversed.'}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
